// `grid init service <name> --type <type>`. Adds a service entry
// to `cloudgrid.yaml` and creates `services/<name>/` with the per-type
// starter. Per decision 015 §"Scaffold commands"; BL621 Phase 3 (Decision 042
// §"Renames") moved this from `scaffold service` to `init service`. The command
// object is registered under the `init` tree (see commands/init.ts).

import { Command } from 'commander';
import { existsSync } from 'node:fs';
import { join } from 'node:path';
import { CloudGridError } from '../../utils.js';
import { handleV2Error } from '../../handle-error.js';
import { addService } from './yaml-edit.js';
import { scaffoldService } from './scaffolder.js';
import type { ServiceType } from './templates.js';

const ALLOWED_TYPES: readonly ServiceType[] = ['node', 'nextjs', 'python', 'static', 'cron'];

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

export interface ScaffoldServiceOpts {
  type?: string;
  schedule?: string;
  cwd?: string;
}

export async function runScaffoldService(name: string, opts: ScaffoldServiceOpts): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();

  if (!SLUG_RE.test(name)) {
    throw new CloudGridError(
      "Service name must be lowercase alphanumeric with hyphens (no leading/trailing hyphen).",
    );
  }
  if (!opts.type) {
    throw new CloudGridError('--type is required (node|nextjs|python|static|cron).');
  }
  if (!ALLOWED_TYPES.includes(opts.type as ServiceType)) {
    throw new CloudGridError(
      `--type must be one of: ${ALLOWED_TYPES.join(', ')}. Got: '${opts.type}'.`,
    );
  }
  const type = opts.type as ServiceType;
  if (type === 'cron' && !opts.schedule) {
    throw new CloudGridError(
      "Cron services require a schedule. Pass --schedule '<cron expression>'.",
    );
  }

  if (!existsSync(join(cwd, 'cloudgrid.yaml'))) {
    throw new CloudGridError('No cloudgrid.yaml found. Run: grid new');
  }

  const serviceDir = join(cwd, 'services', name);
  if (existsSync(serviceDir)) {
    throw new CloudGridError(
      `Service folder services/${name}/ already exists. Use 'grid scaffold rename' to rename, or remove first.`,
    );
  }

  // Update yaml first — yaml-edit throws if `name` already declared, so a
  // half-state where the folder exists but the yaml is unchanged is
  // impossible (we just verified the folder doesn't exist).
  addService(cwd, name, { type, schedule: opts.schedule });

  // Then write per-type starter files.
  const result = scaffoldService(cwd, name, type);

  process.stdout.write(`Added service '${name}' (${type}).\n`);
  for (const f of result.written) {
    process.stdout.write(`  + ${f}\n`);
  }
  process.stdout.write('\n');
  process.stdout.write(`  Edit cloudgrid.yaml to add service-level settings (env, requires).\n`);
  process.stdout.write(`  Run: grid dev\n`);
}

export const initServiceCommand = new Command('service')
  .description('Add a new service to cloudgrid.yaml and scaffold its folder')
  .argument('<name>', 'Service name (lowercase alphanumeric + hyphens)')
  .requiredOption('--type <type>', 'Service type (node|nextjs|python|static|cron)')
  .option('--schedule <expr>', 'Cron schedule (required for type: cron)')
  .action(async (name: string, opts: { type: string; schedule?: string }) => {
    try {
      await runScaffoldService(name, opts);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });
